import { Icon } from '@components/primitives/Icon'
import TextArea from '@components/primitives/Input/TextArea'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faEdit } from '@fortawesome/pro-regular-svg-icons/faEdit'
import { faSave } from '@fortawesome/pro-regular-svg-icons/faSave'
import type { ChangeEvent, ChangeEventHandler } from 'react'
import { useState } from 'react'

export default function EditableMessage(props: {
  ariaLabel?: string
  autoFocus?: boolean
  className?: string
  error?: boolean
  height?: number | string
  hideEditButton?: boolean
  label?: string
  onChange?: ChangeEventHandler<HTMLTextAreaElement>
  placeholder?: string
  value?: string | number | readonly string[]
  width?: number | string
}) {
  const {
    autoFocus = false,
    className,
    hideEditButton = false,
    label,
    onChange,
    ...rest
  } = props
  const [isEditing, setIsEditing] = useState(autoFocus)
  const [wasEdited, setWasEdited] = useState(false)
  const textAreaClassName = classMerge(
    'resize-none rounded-lg border-2 leading-8 hover:!cursor-pointer md:rounded-2xl',
    {
      '!border-neutral100 hover:!border-black': !isEditing && !wasEdited,
    },
    { '!border-success200': isEditing || wasEdited },
    className,
  )

  const handleChange = (ev: ChangeEvent<HTMLTextAreaElement>) => {
    setWasEdited(true)
    if (onChange) {
      onChange(ev)
    }
  }

  const toggleEditing = () => setIsEditing((isEditing) => !isEditing)

  return (
    <div className='relative w-full'>
      {!hideEditButton && (
        <div
          className={classMerge(
            'group absolute -right-4 -top-1 flex h-8 w-24 cursor-pointer items-center justify-center rounded-full px-4 transition-all ease-linear md:w-40',
            {
              'bg-black text-white': !isEditing,
              'bg-success200': isEditing,
            },
          )}
          onClick={() => {
            toggleEditing()
          }}
        >
          <Icon
            className={classMerge({
              'text-dark': isEditing,
              'text-white': !isEditing,
            })}
            icon={isEditing ? faSave : faEdit}
          />
          <span
            className={classMerge(
              'hidden overflow-hidden whitespace-nowrap md:inline',
              {
                'ml-2 w-auto text-dark': isEditing,
                'ml-2 w-auto text-white': !isEditing,
              },
            )}
          >
            {isEditing ? 'Save' : 'Edit'}
          </span>

          <span
            className={classMerge(
              'ml-2 w-auto overflow-hidden whitespace-nowrap md:hidden',
              {
                'text-dark': isEditing,
                'text-white': !isEditing,
              },
            )}
          >
            {isEditing ? 'Save' : 'Edit'}
          </span>
        </div>
      )}
      {label && (
        <div className='absolute left-5 top-2'>
          <Text className='text-neutral500' styleName='p-tiny' value={label} />
        </div>
      )}
      <TextArea
        {...rest}
        autoFocus={autoFocus}
        className={textAreaClassName}
        onChange={handleChange}
        readOnly={!isEditing}
      />
    </div>
  )
}
